/**
 * Sonidos del juego sintetizados con Web Audio (sin archivos: 0 KB de
 * assets y sin licencias de terceros).
 *
 * - Un único AudioContext, creado/reanudado en el primer gesto del usuario
 *   (requisito de Chrome y Safari, también en iPhone). Ver `installAudioUnlock`.
 * - Volumen y silencio globales, guardados en el navegador.
 * - Murmullo de público en bucle durante el partido, que sube en goles,
 *   canastas y jonrones (`cheer`).
 */

export type SfxName =
  | 'whistle'
  | 'whistleLong'
  | 'kick'
  | 'bounce'
  | 'shotRelease'
  | 'rim'
  | 'swish'
  | 'buzzer'
  | 'pitch'
  | 'pitchFast'
  | 'pitchBreaking'
  | 'pitchSoft'
  | 'batCrack'
  | 'swingMiss'
  | 'glove'
  | 'strike'
  | 'out'
  | 'safe'
  | 'throwBall'
  | 'slide'
  | 'step'
  | 'batDrop'
  | 'horn';

type Ctx = AudioContext | OfflineAudioContext;

// ─────────────────────────────────────────────
// Ajustes (volumen / silencio) — store con suscriptores
// ─────────────────────────────────────────────
const STORAGE_KEY = 'sportsarena.audio';

export interface AudioSettings {
  volume: number; // 0..1
  muted: boolean;
  /** Público (murmullo y ovaciones), aparte del resto de sonidos. */
  crowdVolume: number; // 0..1
  crowdMuted: boolean;
}

const loadSettings = (): AudioSettings => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as Partial<AudioSettings> | null;
    return {
      volume: typeof raw?.volume === 'number' ? Math.max(0, Math.min(1, raw.volume)) : 0.7,
      muted: raw?.muted === true,
      crowdVolume: typeof raw?.crowdVolume === 'number' ? Math.max(0, Math.min(1, raw.crowdVolume)) : 1,
      crowdMuted: raw?.crowdMuted === true,
    };
  } catch {
    return { volume: 0.7, muted: false, crowdVolume: 1, crowdMuted: false };
  }
};

let settings: AudioSettings = typeof window === 'undefined' ? { volume: 0.7, muted: false, crowdVolume: 1, crowdMuted: false } : loadSettings();
const listeners = new Set<() => void>();

export const getAudioSettings = (): AudioSettings => settings;
export const subscribeAudio = (l: () => void): (() => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
export const setAudioSettings = (patch: Partial<AudioSettings>): void => {
  settings = {
    ...settings,
    ...patch,
    volume: Math.max(0, Math.min(1, patch.volume ?? settings.volume)),
    crowdVolume: Math.max(0, Math.min(1, patch.crowdVolume ?? settings.crowdVolume)),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /* sin almacenamiento */
  }
  applyMaster();
  listeners.forEach((l) => l());
};

// ─────────────────────────────────────────────
// Contexto y desbloqueo
// ─────────────────────────────────────────────
let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let unlocked = false;

const createContext = (): AudioContext | null => {
  const Ctor =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  const c = new Ctor({ latencyHint: 'interactive' });
  master = c.createGain();
  master.connect(c.destination);
  ctx = c;
  applyMaster();
  return c;
};

/** Silencio mientras se muestra un anuncio (no toca los ajustes del jugador). */
let adMuted = false;
export const setAdMuted = (on: boolean): void => {
  adMuted = on;
  applyMaster();
};

const applyMaster = () => {
  if (!ctx || !master) return;
  const target = settings.muted || adMuted ? 0 : settings.volume;
  master.gain.setTargetAtTime(target, ctx.currentTime, 0.02);
  crowdBus?.gain.setTargetAtTime(settings.crowdMuted ? 0 : settings.crowdVolume, ctx.currentTime, 0.05);
};
/** Bus del público (entre el público y el volumen general). */
let crowdBus: GainNode | null = null;

/** Crea/reanuda el audio dentro de un gesto del usuario. Idempotente. */
export const unlockAudio = (): void => {
  try {
    const c = ctx ?? createContext();
    if (!c) return;
    if (c.state !== 'running') void c.resume().catch(() => {});
    if (!unlocked || c.state !== 'running') {
      // iOS: reproducir un buffer vacío dentro del gesto termina de desbloquear.
      const src = c.createBufferSource();
      src.buffer = c.createBuffer(1, 1, 22050);
      src.connect(c.destination);
      src.start(0);
      unlocked = true;
    }
  } catch {
    /* sin Web Audio */
  }
};

/** El audio se pausó por pasar a segundo plano (no por el usuario). */
let pausedByVisibility = false;

/**
 * Desbloqueo y recuperación del audio.
 *
 * - Solo los gestos que el navegador acepta como activación desbloquean el
 *   audio: en Android/Chrome/Brave un `pointerdown` TÁCTIL no cuenta (sí
 *   `pointerup`, `touchend`, `click` y `keydown`). Por eso no se crea el
 *   AudioContext en un `pointerdown` táctil: nacería suspendido.
 * - Los oyentes se quedan para siempre (son baratos): si el contexto vuelve a
 *   suspenderse (segundo plano, otra app toma el audio, llamada), el siguiente
 *   toque lo reanuda. Antes se quitaban tras el primer desbloqueo y, si el
 *   navegador rechazaba la reanudación al volver a la app (Brave lo hace sin
 *   gesto), el juego se quedaba mudo para siempre.
 */
export const installAudioUnlock = (): void => {
  if (typeof window === 'undefined') return;
  const onGesture = (e: Event) => {
    if (e.type === 'pointerdown' && (e as PointerEvent).pointerType !== 'mouse') return;
    if (document.visibilityState === 'hidden') return;
    if (ctx?.state === 'running' && unlocked) return;
    pausedByVisibility = false;
    unlockAudio();
  };
  for (const type of ['pointerdown', 'pointerup', 'touchend', 'click', 'keydown'] as const) {
    window.addEventListener(type, onGesture, { capture: true, passive: true });
  }
  // Otra app o pestaña: se pausa todo el audio; al volver se intenta reanudar
  // (si el navegador no lo permite sin gesto, lo hará el siguiente toque).
  const onHidden = () => {
    if (!ctx || ctx.state !== 'running') return;
    pausedByVisibility = true;
    void ctx.suspend().catch(() => {});
  };
  document.addEventListener('visibilitychange', () => {
    if (!ctx) return;
    if (document.visibilityState === 'hidden') onHidden();
    else if (pausedByVisibility || ctx.state !== 'running') {
      void ctx
        .resume()
        .then(() => {
          if (ctx?.state === 'running') pausedByVisibility = false;
        })
        .catch(() => {});
    }
  });
  window.addEventListener('pagehide', onHidden);
  window.addEventListener('pageshow', () => {
    if (ctx && document.visibilityState === 'visible' && ctx.state !== 'running') void ctx.resume().catch(() => {});
  });
};

export const isAudioRunning = (): boolean => ctx?.state === 'running';

// ─────────────────────────────────────────────
// Registro (pruebas automáticas): últimos sonidos disparados
// ─────────────────────────────────────────────
interface SfxDebug {
  log: string[];
  render?: (name: SfxName) => Promise<number>;
  /** Pruebas: suspende el AudioContext como haría el sistema. */
  suspend?: () => Promise<string>;
  /** Estado del AudioContext ('none' si aún no se creó). */
  state?: () => string;
}
const debug: SfxDebug = { log: [] };
if (typeof window !== 'undefined') (globalThis as unknown as { __sportsarenaSfx: SfxDebug }).__sportsarenaSfx = debug;

// ─────────────────────────────────────────────
// Bloques de síntesis
// ─────────────────────────────────────────────
const noiseCache = new WeakMap<BaseAudioContext, AudioBuffer>();
const noiseBuffer = (c: Ctx): AudioBuffer => {
  let buf = noiseCache.get(c);
  if (!buf) {
    buf = c.createBuffer(1, c.sampleRate * 2, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    noiseCache.set(c, buf);
  }
  return buf;
};

const env = (c: Ctx, dest: AudioNode, t: number, attack: number, peak: number, decay: number): GainNode => {
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  g.connect(dest);
  return g;
};

const tone = (
  c: Ctx,
  dest: AudioNode,
  t: number,
  type: OscillatorType,
  from: number,
  to: number,
  dur: number,
  peak: number,
  attack = 0.005
) => {
  const o = c.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(from, t);
  if (to !== from) o.frequency.exponentialRampToValueAtTime(Math.max(1, to), t + dur);
  o.connect(env(c, dest, t, attack, peak, dur));
  o.start(t);
  o.stop(t + attack + dur + 0.05);
};

const noise = (
  c: Ctx,
  dest: AudioNode,
  t: number,
  dur: number,
  peak: number,
  filter: { type: BiquadFilterType; freq: number; to?: number; q?: number },
  attack = 0.003
) => {
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(c);
  const f = c.createBiquadFilter();
  f.type = filter.type;
  f.frequency.setValueAtTime(filter.freq, t);
  if (filter.to) f.frequency.exponentialRampToValueAtTime(filter.to, t + dur);
  f.Q.value = filter.q ?? 1;
  src.connect(f);
  f.connect(env(c, dest, t, attack, peak, dur));
  src.start(t, Math.random() * 1.5);
  src.stop(t + attack + dur + 0.05);
};

/** Silbato de árbitro: dos tonos agudos con vibrato rápido (el "trino"). */
const whistle = (c: Ctx, dest: AudioNode, t: number, dur: number) => {
  for (const [freq, gain] of [
    [2900, 0.12],
    [3050, 0.08],
  ] as const) {
    const o = c.createOscillator();
    o.type = 'sine';
    o.frequency.value = freq;
    const lfo = c.createOscillator();
    lfo.frequency.value = 28;
    const depth = c.createGain();
    depth.gain.value = 90;
    lfo.connect(depth).connect(o.frequency);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.02);
    g.gain.setValueAtTime(gain, t + dur - 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(dest);
    o.start(t);
    lfo.start(t);
    o.stop(t + dur + 0.05);
    lfo.stop(t + dur + 0.05);
  }
  noise(c, dest, t, dur, 0.03, { type: 'bandpass', freq: 3000, q: 4 });
};

/** Recetas de cada sonido sobre (contexto, destino, instante). */
const RECIPES: Record<SfxName, (c: Ctx, d: AudioNode, t: number) => void> = {
  whistle: (c, d, t) => whistle(c, d, t, 0.45),
  whistleLong: (c, d, t) => {
    whistle(c, d, t, 0.35);
    whistle(c, d, t + 0.45, 0.35);
    whistle(c, d, t + 0.9, 0.9);
  },
  kick: (c, d, t) => {
    tone(c, d, t, 'sine', 140, 50, 0.14, 0.7);
    noise(c, d, t, 0.04, 0.25, { type: 'highpass', freq: 1800 });
  },
  bounce: (c, d, t) => {
    tone(c, d, t, 'sine', 190, 85, 0.09, 0.55);
    noise(c, d, t, 0.03, 0.12, { type: 'bandpass', freq: 900, q: 2 });
  },
  shotRelease: (c, d, t) => noise(c, d, t, 0.25, 0.3, { type: 'bandpass', freq: 600, to: 1800, q: 1.2 }, 0.04),
  rim: (c, d, t) => {
    for (const [f, g] of [
      [610, 0.16],
      [1230, 0.1],
      [1870, 0.06],
      [2510, 0.04],
    ] as const) {
      tone(c, d, t, 'triangle', f, f * 0.995, 0.5, g);
    }
    noise(c, d, t, 0.05, 0.15, { type: 'highpass', freq: 2500 });
  },
  swish: (c, d, t) => noise(c, d, t, 0.35, 0.22, { type: 'bandpass', freq: 2200, to: 5500, q: 1.5 }, 0.02),
  buzzer: (c, d, t) => {
    const f = c.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 1800;
    f.connect(d);
    tone(c, f, t, 'sawtooth', 220, 220, 1.0, 0.25, 0.01);
    tone(c, f, t, 'sawtooth', 226, 226, 1.0, 0.18, 0.01);
  },
  pitch: (c, d, t) => noise(c, d, t, 0.35, 0.35, { type: 'bandpass', freq: 400, to: 1600, q: 1 }, 0.05),
  // Recta/sinker: zumbido corto y agudo.
  pitchFast: (c, d, t) => noise(c, d, t, 0.22, 0.4, { type: 'bandpass', freq: 900, to: 2600, q: 1.4 }, 0.02),
  // Curva/slider: silbido que "gira" (barrido de vuelta).
  pitchBreaking: (c, d, t) => {
    noise(c, d, t, 0.2, 0.3, { type: 'bandpass', freq: 600, to: 1800, q: 2 }, 0.03);
    noise(c, d, t + 0.18, 0.25, 0.25, { type: 'bandpass', freq: 1800, to: 700, q: 2 }, 0.02);
  },
  // Cambio: soplo suave y largo.
  pitchSoft: (c, d, t) => noise(c, d, t, 0.45, 0.25, { type: 'bandpass', freq: 350, to: 900, q: 0.8 }, 0.08),
  batCrack: (c, d, t) => {
    noise(c, d, t, 0.06, 0.9, { type: 'highpass', freq: 1500 }, 0.001);
    tone(c, d, t, 'triangle', 1800, 1400, 0.05, 0.35, 0.001);
    tone(c, d, t, 'sine', 220, 120, 0.12, 0.4, 0.001);
  },
  swingMiss: (c, d, t) => noise(c, d, t, 0.22, 0.4, { type: 'bandpass', freq: 300, to: 1200, q: 0.8 }, 0.03),
  glove: (c, d, t) => {
    noise(c, d, t, 0.05, 0.45, { type: 'lowpass', freq: 900 }, 0.001);
    tone(c, d, t, 'sine', 110, 70, 0.08, 0.5, 0.001);
  },
  strike: (c, d, t) => {
    tone(c, d, t, 'square', 880, 880, 0.12, 0.08);
    tone(c, d, t + 0.13, 'square', 660, 660, 0.2, 0.08);
  },
  out: (c, d, t) => {
    tone(c, d, t, 'square', 660, 660, 0.14, 0.08);
    tone(c, d, t + 0.15, 'square', 440, 440, 0.32, 0.08);
  },
  // Safe: dos notas ascendentes, más alegres que el "out".
  safe: (c, d, t) => {
    tone(c, d, t, 'triangle', 523, 523, 0.12, 0.12);
    tone(c, d, t + 0.12, 'triangle', 784, 784, 0.25, 0.12);
  },
  // Tiro a una base: silbido corto del aire.
  throwBall: (c, d, t) => noise(c, d, t, 0.18, 0.3, { type: 'bandpass', freq: 1200, to: 2800, q: 1.6 }, 0.015),
  // Deslizada: arrastre de tierra.
  slide: (c, d, t) => noise(c, d, t, 0.45, 0.35, { type: 'lowpass', freq: 1400, to: 300, q: 0.7 }, 0.02),
  // Paso sobre tierra/césped: golpe sordo muy corto.
  step: (c, d, t) => {
    noise(c, d, t, 0.06, 0.18, { type: 'lowpass', freq: 500 }, 0.004);
    tone(c, d, t, 'sine', 90, 60, 0.05, 0.12, 0.002);
  },
  // Bate de madera que cae al suelo: dos golpes huecos.
  batDrop: (c, d, t) => {
    tone(c, d, t, 'triangle', 420, 300, 0.08, 0.3, 0.001);
    noise(c, d, t, 0.05, 0.2, { type: 'bandpass', freq: 1200, q: 3 }, 0.001);
    tone(c, d, t + 0.14, 'triangle', 380, 280, 0.07, 0.18, 0.001);
  },
  horn: (c, d, t) => {
    const f = c.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 1400;
    f.connect(d);
    for (const freq of [233, 293, 349]) tone(c, f, t, 'sawtooth', freq, freq, 1.1, 0.12, 0.04);
  },
};

/** Reproduce un sonido (si el audio está desbloqueado y no silenciado). */
export const playSfx = (name: SfxName, gain = 1): void => {
  debug.log.push(name);
  if (debug.log.length > 80) debug.log.shift();
  if (!ctx || !master || ctx.state !== 'running' || settings.muted) return;
  const g = ctx.createGain();
  g.gain.value = gain;
  g.connect(master);
  RECIPES[name](ctx, g, ctx.currentTime + 0.005);
  // Desconecta el nodo cuando ya terminó cualquier receta (≤ 2 s).
  setTimeout(() => g.disconnect(), 2500);
};

// ─────────────────────────────────────────────
// Público: murmullo en bucle + ovaciones
// ─────────────────────────────────────────────
let crowd: { gain: GainNode; cheer: GainNode; stop: () => void } | null = null;
const CROWD_BASE = 0.05;

/** Arranca el murmullo de fondo (llamar al entrar al partido). */
export const startCrowd = (): void => {
  if (!ctx || !master || crowd) return;
  const c = ctx;
  if (!crowdBus) {
    crowdBus = c.createGain();
    crowdBus.gain.value = settings.crowdMuted ? 0 : settings.crowdVolume;
    crowdBus.connect(master);
  }
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(c);
  src.loop = true;
  // Murmullo: ruido de banda media con ondulación lenta (voces lejanas).
  const band = c.createBiquadFilter();
  band.type = 'bandpass';
  band.frequency.value = 700;
  band.Q.value = 0.6;
  const low = c.createBiquadFilter();
  low.type = 'lowpass';
  low.frequency.value = 2200;
  const gain = c.createGain();
  gain.gain.value = CROWD_BASE;
  const lfo = c.createOscillator();
  lfo.frequency.value = 0.23;
  const lfoDepth = c.createGain();
  lfoDepth.gain.value = 0.015;
  lfo.connect(lfoDepth).connect(gain.gain);
  src.connect(band).connect(low).connect(gain).connect(crowdBus);
  // Capa brillante para las ovaciones (gritos), normalmente en silencio.
  const bright = c.createBiquadFilter();
  bright.type = 'bandpass';
  bright.frequency.value = 1700;
  bright.Q.value = 0.8;
  const cheer = c.createGain();
  cheer.gain.value = 0;
  src.connect(bright).connect(cheer).connect(crowdBus);
  src.start();
  lfo.start();
  crowd = {
    gain,
    cheer,
    stop: () => {
      const t = c.currentTime;
      gain.gain.setTargetAtTime(0, t, 0.3);
      cheer.gain.setTargetAtTime(0, t, 0.3);
      setTimeout(() => {
        src.stop();
        lfo.stop();
        src.disconnect();
      }, 1500);
    },
  };
};

export const stopCrowd = (): void => {
  crowd?.stop();
  crowd = null;
};

/** Ovación: el público sube de volumen y vuelve al murmullo (intensidad 0..1). */
export const cheer = (intensity = 1): void => {
  debug.log.push(`cheer:${intensity.toFixed(1)}`);
  if (!ctx || !crowd) return;
  const t = ctx.currentTime;
  const peak = CROWD_BASE + 0.25 * intensity;
  crowd.gain.gain.cancelScheduledValues(t);
  crowd.gain.gain.setTargetAtTime(peak, t, 0.15);
  crowd.gain.gain.setTargetAtTime(CROWD_BASE, t + 1.2 + intensity * 1.5, 0.8);
  crowd.cheer.gain.cancelScheduledValues(t);
  crowd.cheer.gain.setTargetAtTime(0.22 * intensity, t, 0.12);
  crowd.cheer.gain.setTargetAtTime(0, t + 1.0 + intensity * 1.5, 0.7);
};

/** Silencio / abucheo apagado: el murmullo baja un momento y vuelve (out de los tuyos). */
export const hush = (intensity = 0.5): void => {
  debug.log.push(`hush:${intensity.toFixed(1)}`);
  if (!ctx || !crowd) return;
  const t = ctx.currentTime;
  crowd.gain.gain.cancelScheduledValues(t);
  crowd.gain.gain.setTargetAtTime(CROWD_BASE * (1 - 0.6 * intensity), t, 0.2);
  crowd.gain.gain.setTargetAtTime(CROWD_BASE, t + 1.2 + intensity, 0.8);
};

// ─────────────────────────────────────────────
// Prueba: renderiza un sonido sin altavoces y devuelve su pico
// ─────────────────────────────────────────────
debug.state = () => ctx?.state ?? 'none';
/** Pruebas: simula que el sistema suspende el audio (otra app, segundo plano). */
debug.suspend = async () => {
  await ctx?.suspend();
  return ctx?.state ?? 'none';
};

debug.render = async (name: SfxName): Promise<number> => {
  const OfflineCtor =
    window.OfflineAudioContext ??
    (window as unknown as { webkitOfflineAudioContext?: typeof OfflineAudioContext }).webkitOfflineAudioContext;
  const off = new OfflineCtor(1, 44100 * 2.5, 44100);
  RECIPES[name](off, off.destination, 0.01);
  const out = await off.startRendering();
  // Pico absoluto: representa mejor un sonido corto que el RMS de 2,5 s.
  const data = out.getChannelData(0);
  let peak = 0;
  for (let i = 0; i < data.length; i++) peak = Math.max(peak, Math.abs(data[i]!));
  return peak;
};
